<script lang="ts" setup>
import AUApi from "@/api";
import { ref, onMounted, reactive, nextTick } from "vue";

import { getKennungValueDisplay, getKennungTitle } from "@/DataHelper";
import CommonBaseButtons from "@/components/common/BaseButtons.vue";
import { mountedBase, onSave, BaseSetup, onDelete } from "@/DataLoaderStatic";
import { goBack } from "@/NavigationHelper";
import EditTimePicker from "@/editor/EditTimePicker.vue";

import {
  createEditorValidator,
  provideEditorValidator,
} from "@/components/EditorValidator";
import { showSnackbar } from "@/UIHelper";
const validator = createEditorValidator();
provideEditorValidator(validator);

const emit = defineEmits(["back", "save", "delete", "backDelete"]);
const props = defineProps<{
  zeitbereichUebergabe: APIZeitbereich;
  aic?: number;
}>();

let showUestAuszahlen = ref<boolean>(false);
let showauszahlungUest = ref<boolean>(false);
let showZulage1 = ref<string>("");
let showZulage2 = ref<string>("");
let showZulage3 = ref<string>("");
let saveVisible = ref<boolean>(false);
let setKey = ref<number>(0);
let isSaving = ref<boolean>(false);

const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "WebWF_Freigabe_Antrag",
  emitBack: () => emit("back"),
};
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));
onMounted(async () => {
  if (props.aic) {
    // gibt es übergabe AIC - dann zieht der vor!
    pageParams.queryAIC = props.aic;
    pageParams.aic = props.aic;
  }
  pageParams.zeitbereich = props.zeitbereichUebergabe;
  await mountedBase(uiData, pageParams, false);
  if (!uiData.showLoading && uiData.data) {
    const uestAuszahlen = getKennungTitle(
      "ZE_UEST_NICHT_AUSZAH",
      uiData.columns
    );
    if (uestAuszahlen) {
      showUestAuszahlen.value = true;
    }
    const auszahlungUest = getKennungTitle(
      "ZE_AUSZAHLUNG_UEST",
      uiData.columns
    );
    if (auszahlungUest) {
      showauszahlungUest.value = true;
    }
    showZulage1.value = getKennungTitle("Zulage_1", uiData.columns);
    showZulage2.value = getKennungTitle("Zulage_2", uiData.columns);
    showZulage3.value = getKennungTitle("Zulage_3", uiData.columns);
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});
async function bewDelete() {
  if (uiData.data[0] && uiData.data[0].aic_Bew_pool) {
    emit("delete", uiData.data[0].aic_Bew_pool);
  }
}
async function onValidate() {
  return await validator.validateAsync();
}
async function save() {
  if (isSaving.value) return;
  isSaving.value = true;
  const isvalid = await onValidate();
  if (!isvalid.valid) {
    showSnackbar({
      text:
        "Validierung fehlgeschlagen - Bitte prüfen! (" +
        isvalid.invalidFields.join(", ") +
        ")",
      duration: 4000,
      color: "error",
    });
    isSaving.value = false;
    return;
  }

  onSave(uiData, pageParams, 0, "", undefined, pageParams.aic)?.then(() => {
    emit("save");
  }).finally(() => {
    isSaving.value = false;
  });
}

function checkChanged(val: any) {
  saveVisible.value = true;
  setKey.value += +1;
}
</script>
<template>
  <v-card :loading="uiData.showLoading">
    <v-card-text v-if="uiData.initialized" class="cardText">
      <v-row>
        <v-col>
          <EditCheckBox kennung="ZE_KEINE_VERFALLBEGI" :disabled="true" />
        </v-col>
        <v-col>
          <EditCheckBox kennung="ZE_K_VERFALLZEITENDE" :disabled="true" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <EditCheckBox
            v-if="showUestAuszahlen"
            kennung="ZE_UEST_NICHT_AUSZAH"
            :disabled="true"
          />
        </v-col>
        <v-col>
          <EditTimePicker
            v-if="showauszahlungUest"
            kennung="ZE_AUSZAHLUNG_UEST"
            :disabled="true"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col v-if="showZulage1">
          {{ showZulage1 }}:
          {{
            getKennungValueDisplay(uiData.data[0], "Zulage_1", uiData.columns)
          }}
        </v-col>

        <v-col v-if="showZulage2">
          {{ showZulage2 }}:
          {{
            getKennungValueDisplay(uiData.data[0], "Zulage_2", uiData.columns)
          }}
        </v-col>
        <v-col v-if="showZulage3">
          {{ showZulage3 }}:
          {{
            getKennungValueDisplay(uiData.data[0], "Zulage_3", uiData.columns)
          }}
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <EditCheckBox :disabled="true" kennung="ZE_KEINE_PAUSE" />
        </v-col>
        <v-col>
          <EditTimePicker :disabled="true" kennung="ZE_ALTERNATIVE_PAUSE" />
        </v-col>
      </v-row>
      <EditTextArea :disabled="true" kennung="MEMO_MITARBEITER" />
      <EditTextArea :disabled="false" kennung="VG" @change="checkChanged" />
      <EditCheckBox3 kennung="ZE_ANTRAG_BOOL3" @change="checkChanged" />
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <CommonBaseButtons
        :key="setKey"
        :saveVisible="saveVisible"
        :deleteVisible="false"
        :createVisible="false"
        :refreshVisible="false"
        @back="goBack()"
        @delete="bewDelete"
        @save="save"
      ></CommonBaseButtons>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.cardText {
  text-align: left;
  padding: 0px;
  padding-left: 10px;
  padding-right: 10px;
}
.col {
  padding-right: 0px;
  padding-left: 10px;
  padding-top: 10px;
  padding-bottom: 0px;
}
</style>
